"use client";
import { BINDING_CONFIG } from "@/utils/constants";
import React, { createContext, useContext, useState, ReactNode } from "react";

// Define the context type
interface RegionContextType {
  region: string | undefined;
  setRegion: (region: string) => void;
}

// Create the context with an undefined default value
const RegionContext = createContext<RegionContextType | undefined>(undefined);

// Create the provider component
export const RegionProvider: React.FC<{
  children: ReactNode;
  bindingDomain: keyof typeof BINDING_CONFIG;
}> = ({ children, bindingDomain }) => {
  const [region, setRegion] = useState<string | undefined>(undefined);

  // Load region from localStorage when the provider mounts
  React.useEffect(() => {
    const storedRegion = localStorage.getItem("banner");
    if (storedRegion) {
      setRegion(storedRegion);
    } else {
      setRegion(BINDING_CONFIG[bindingDomain].REGION); // Default region
    }
  }, []);

  return (
    <RegionContext.Provider value={{ region, setRegion }}>
      {children}
    </RegionContext.Provider>
  );
};

// Custom hook to consume the region context
export const useRegion = (): RegionContextType => {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error("useRegion must be used within a RegionProvider");
  }
  return context;
};
